// uploader.ts ↔ game/uploader.{h,cpp} — UploadByteBuffer/Uploader: the
// sync-error diagnostic POST (SimUIForm::ReportSyncError, SimUI.cpp:568-572,
// uploads the StateTracker ToJson to kszSyncErrorUploadUrl?gameid=&pid=).
//
// SHAPE NOTE (the one C++→TS seam): Uploader::Upload spins a MODAL event loop
// (uploader.cpp:47-58) behind a TransportWaitingUI until the
// HttpResponseHandler callbacks latch done_/error_. JS cannot block the event
// loop, so the TS Upload submits and returns; the loop's exit epilogue
// (uploader.cpp:60-67: ReleaseRequest, the "Upload Error" HtMessageBox when
// errorstr_ is set, and Upload's !error_ result) runs when a callback latches
// done_/error_ — scheduled on a microtask so a service that fires
// OnReceivedResponse(400)+OnError back-to-back (webhttprequest.cpp:46-55, and
// FetchHttpService's port of it) finishes BOTH before the epilogue reads
// errorstr_, exactly like the C++ modal loop regaining control after the
// dispatch. TransportWaitingUI becomes the host.setMessage seam; HtMessageBox
// becomes host.msgBox.

import type { HttpService, HttpRequest, HttpResponseHandler, HeadersMap } from './httpservice.ts';
import { ToInteger } from './httpservice.ts';

/** The Uploader's UI/event reaches: TransportWaitingUI (uploader.cpp:70-74),
 *  HtMessageBox (uploader.cpp:63-65) and the modal-loop completion. All
 *  optional — the production sync-error arm is fire-and-forget. */
export interface UploaderHost {
  /** TransportWaitingUI(message) — "Uploading game state..." then the
   *  "Uploaded %d of %d bytes..." progress text (uploader.cpp:45/100). */
  setMessage?(message: string): void;
  /** HtMessageBox(kfMbWhiteBorder, "Upload Error", errorstr_) —
   *  uploader.cpp:63-65. */
  msgBox?(title: string, message: string): void;
  /** Upload's modal-loop result (uploader.cpp:67 `return !error_`). */
  onComplete?(ok: boolean): void;
}

// game/uploader.cpp:7-20 — submit-and-(modally-)wait collapses to
// submit-and-callback; false = the request could not even be created/submitted
// (uploader.cpp:10-13/36-39 — the C++ also deletes bb; GC does here).
export function UploadByteBuffer(
  service: HttpService,
  bb: Uint8Array,
  url: string,
  host: UploaderHost = {},
): boolean {
  const uploader = new Uploader(service, host);
  return uploader.Upload(bb, url);
}

export class Uploader implements HttpResponseHandler {
  // uploader.cpp:22-24 — the ctor's member init list
  private readonly service_: HttpService;
  private req_: HttpRequest | null = null;
  private done_ = false;
  private error_ = false;
  private message_ = '';
  private errorstr_ = '';
  private cbLength_ = 0;
  private cbTotal_ = 0;
  private readonly host_: UploaderHost;
  /** The modal loop exits exactly once (uploader.cpp:49 `while (!done_ &&
   *  !error_)`); late callbacks after the exit hit a released request. */
  private exited_ = false;
  private exitScheduled_ = false;

  constructor(service: HttpService, host: UploaderHost = {}) {
    this.service_ = service;
    this.host_ = host;
  }

  // uploader.cpp:34-68 — Upload: NewRequest/SetURL/SetBody/SetMethod(POST)/
  // SubmitRequest, the waiting UI, then the modal loop (⇒ ScheduleExit).
  Upload(bb: Uint8Array, url: string): boolean {
    this.req_ = this.service_.NewRequest(this);
    if (this.req_ === null) {
      return false;
    }
    this.req_.SetURL(url);
    this.req_.SetBody(bb);
    this.req_.SetMethod('POST');
    this.service_.SubmitRequest(this.req_);

    this.SetMessage('Uploading game state...');
    return true;
  }

  // uploader.cpp:70-74 — SetMessage: the TransportWaitingUI swap
  private SetMessage(message: string): void {
    this.message_ = message;
    this.host_.setMessage?.(this.message_);
  }

  // uploader.cpp:76-80 Wakeup + :49-67 the modal loop's exit: release the
  // request, raise the error box if errorstr_ landed, report !error_.
  private ScheduleExit(): void {
    if (this.exitScheduled_) return;
    this.exitScheduled_ = true;
    queueMicrotask(() => {
      this.Exit();
    });
  }

  private Exit(): void {
    if (this.exited_) return;
    this.exited_ = true;
    if (this.req_ !== null) {
      this.service_.ReleaseRequest(this.req_);
      this.req_ = null;
    }
    if (this.errorstr_.length !== 0) {
      this.host_.msgBox?.('Upload Error', this.errorstr_);
    }
    this.host_.onComplete?.(!this.error_);
  }

  // uploader.cpp:82-107
  OnReceivedResponse(_preq: HttpRequest, code: number, pheaders: HeadersMap | null): void {
    if (code >= 400) {
      this.errorstr_ = `error code ${code}`;
      this.error_ = true;
      this.ScheduleExit();
      return;
    }
    if (code >= 200 && code < 300) {
      // Success! Get Content-Length and show the progress text
      this.cbLength_ = -1;
      const szLength = pheaders === null ? null : pheaders.GetValue('Content-Length');
      if (szLength !== null) {
        this.cbLength_ = ToInteger(szLength, 10) ?? this.cbLength_;
      }
      this.cbTotal_ = 0;
      this.SetMessage(`Uploaded ${this.cbTotal_} of ${this.cbLength_} bytes...`);
      return;
    }
    // Ignore other status codes. If it's a redirect, OnReceivedResponse
    // will get called again.
  }

  // uploader.cpp:109-113
  OnReceivedData(_preq: HttpRequest, _pbb: Uint8Array): void {
    if (this.error_) {
      return;
    }
  }

  // uploader.cpp:115-121
  OnFinishedLoading(_preq: HttpRequest): void {
    if (this.error_) {
      return;
    }
    this.done_ = true;
    this.ScheduleExit();
  }

  // uploader.cpp:123-127 — note the C++ order: a web-stack failure fires
  // OnReceivedResponse(400, NULL) THEN OnError(status) in one dispatch
  // (webhttprequest.cpp:46-55), so errorstr_ ends as the status text.
  OnError(_preq: HttpRequest, pszError: string): void {
    this.errorstr_ = pszError;
    this.error_ = true;
    this.ScheduleExit();
  }
}
